// @flow
import type {ApplicationState} from "../reducers";
import ProductApi, {INTENT} from "../api/ProductApi";
import {
	filterInstanceSet,
	filterReset,
	filterSet,
	filterStatsSet,
	searchPhraseSet,
	searchPreviewCleared,
	searchPreviewSet
} from "../reducers/filterReducer";
import {categoryCountsOf, MAX_PREVIEW_PRODUCTS, toSearchPreview, withTotals} from "../helper/searchPreview";

// Tracks the most recently dispatched getFilterStats request so stale responses
// from a previous filter (vehicle / category / price) can be dropped instead of
// overwriting the current filter stats.
let filterStatsRequestToken = 0;

// The same for the search preview, where it matters on every keystroke: typing "anh" fires three
// requests, and the one for "an" must not overwrite the preview for "anh" when it comes back late.
let searchPreviewRequestToken = 0;

// What picking a vehicle or a category opens does not depend on what has been typed, only on the
// filter, so its count is asked for once per filter and not on every keystroke. A failed request is
// forgotten, the next keystroke asks again.
const countCache: Map<string, Promise<any>> = new Map();

const cachedCount = (key: string, request: () => Promise<any>): Promise<any> => {
	if (!countCache.has(key)) {
		countCache.set(key, request().catch(error => {
			countCache.delete(key);
			throw error;
		}));
	}

	return countCache.get(key);
};

const productApiFor = (state: ApplicationState) => {
	const {dealerId, moduleLanguage, role, environment} = state.config;
	const intent = environment !== "own" ? INTENT.CATALOG_ONLY : INTENT.DEFAULT;

	return new ProductApi(dealerId, {configIdType: role, intent: intent, language: moduleLanguage});
};

const filterParametersOf = filter => ({
	categoryKey: filter.categoryKey,
	categories: filter.categoryKey
		? null
		: filter.categories,
	priceRange: filter.priceRange,
	vehicleKey: filter.vehicleKey,
	vehicle: filter.vehicleKey
		? null
		: {series: filter.vehicle, model: filter.vehicleModel,},
	markers: filter.markers,
});

export const setFilter = (key, value) => {
	return filterSet({
		key,
		value,
	});
};

export const setSearchPhrase = (value: string) => {
	return searchPhraseSet(value);
};

export const resetFilter = () => {
	return filterReset();
};

export const setFilterInstance = (instance, subCat) => {
	return filterInstanceSet({
		filterInstance: instance,
		subCategory: subCat || {},
	});
};

export const getFilterStats = filter => {
	return (dispatch, getState: () => ApplicationState) => {
		const {currentPage} = getState().content;
		const productApi = productApiFor(getState());
		const filterOptions = {
			fuzzy: filter.searchPhrase === "undefined" ? "" : filter.searchPhrase,
			sortOrders: [{
				fieldname: filter.sort.fieldname,
				direction: filter.sort.direction,
			}],
			filterParameters: filterParametersOf(filter),
		};

		const myToken = ++filterStatsRequestToken;
		return productApi.getProductsWithFilter(currentPage, filterOptions)
			.then(response => {
				if (myToken !== filterStatsRequestToken) return;
				dispatch(filterStatsSet(response));
			});
	};
};

/**
 * The preview under the search bar for what has been typed so far: the first products, and the
 * vehicles and categories they belong to. It runs the search the result page runs once the phrase
 * is submitted - under the vehicle and price the customer has chosen, but across all categories,
 * since submitting a search leaves the category behind - so "show all results" keeps its word.
 */
export const getSearchPreview = (typed: string) => {
	return (dispatch, getState: () => ApplicationState) => {
		const myToken = ++searchPreviewRequestToken;
		const state = getState();
		const productApi = productApiFor(state);
		const searchFilter = {...state.filter.filter, categoryKey: null, categories: []};
		const scope = JSON.stringify([state.config.dealerId, state.config.moduleLanguage, filterParametersOf(searchFilter)]);
		const countFor = (filterParameters, read) => productApi
			.getSearchPreview({fuzzy: "", filterParameters}, 1)
			.then(read);

		return productApi
			.getSearchPreview({
				fuzzy: typed,
				filterParameters: filterParametersOf(searchFilter),
			}, MAX_PREVIEW_PRODUCTS)
			.then(response => {
				const preview = toSearchPreview(response, typed);

				// A category opens under the vehicle and price already chosen, and its page counts
				// what the category facet of a search without a phrase counts.
				const categoryCounts = cachedCount(`${scope}|categories`,
					() => countFor(filterParametersOf(searchFilter), categoryCountsOf));

				// A vehicle takes the place of the one chosen. Its facet leaves out the products that fit
				// every vehicle, which its page does show - so each is counted the way its page counts.
				const vehicleCounts = Promise.all(preview.vehicles.map(vehicle =>
					cachedCount(`${scope}|vehicle|${vehicle.key}`,
						() => countFor(
							filterParametersOf({...searchFilter, vehicleKey: vehicle.key}),
							answer => answer.pagedProducts.totalItems))
						.then(count => [vehicle.key, count])));

				return Promise.all([categoryCounts, vehicleCounts])
					.then(([categories, vehicles]) => withTotals(preview, categories, Object.fromEntries(vehicles)))
					// without the totals the entries are still worth offering, just without a number
					.catch(() => withTotals(preview, {}, {}));
			})
			.then(preview => {
				if (myToken !== searchPreviewRequestToken) return;
				dispatch(searchPreviewSet(preview));
			})
			.catch(() => {
				// The preview is an offer, not a result: a failed request leaves the customer with the
				// search bar they had, and nothing is shown that could mislead them.
				if (myToken !== searchPreviewRequestToken) return;
				dispatch(searchPreviewCleared());
			});
	};
};

export const clearSearchPreview = () => {
	searchPreviewRequestToken++;

	return searchPreviewCleared();
};
